import { Card, CardProps, StyleProps } from '@chakra-ui/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { AutocompleteField } from '.';
import { MappedAddress, useGoogleAddressSearch, UseGoogleAddressSearchConfig } from '../../hooks';
import { parseGqlError, TranslationFunction } from '../../utils';
import { GoogleOfficialIcon, SearchIcon } from '../icons';
import type { AutocompleteFieldProps } from './AutocompleteField';

const styleBox = {
  borderRadius: 'lg',
  w: 'full',
  bg: 'bg.light',
  px: { base: 'md', md: 'xl' },
  pt: 'md',
};

interface AddressSearchFieldProps extends Omit<AutocompleteFieldProps, 'onSelect' | 'control' | 'onChange'> {
  t?: TranslationFunction;
  onSelect?: (data: MappedAddress) => void;
  googleAddressConfig: UseGoogleAddressSearchConfig;
  containerProps?: Omit<CardProps, 'children'>;
}

const AddressSearchField: React.FC<AddressSearchFieldProps> = ({
  t,
  label,
  placeholder,
  onSelect,
  name,
  googleAddressConfig,
  containerProps,
  ...inputProps
}) => {
  const { control, setError, clearErrors } = useFormContext();

  const { getGoogleAddresses } = useGoogleAddressSearch(googleAddressConfig);

  const handleSearch = async (value: string) => {
    try {
      const data = await getGoogleAddresses({
        input: value,
      });

      clearErrors(name);

      return data
        ? data?.map((item: MappedAddress) => ({
            label: item.formattedAddress,
            value: item,
          }))
        : [];
    } catch (error) {
      const parseError = t ? parseGqlError(error as any, t) : { message: 'Address not found' };
      setError(name, { type: 'custom', message: parseError.message });
      return [];
    }
  };

  const handleSelect = async (input: any) => {
    if (input && input.value) {
      onSelect?.(input.value as MappedAddress);
    }
  };

  return (
    <Card
      {...styleBox}
      {...containerProps}
    >
      <AutocompleteField
        control={control}
        getOptions={handleSearch}
        isTruncated
        label={label || (t ? t('location.address-input-label') : 'Search for address')}
        leftElement={
          <SearchIcon
            color='primary.pure'
            boxSize={5}
          />
        }
        name={name}
        placeholder={placeholder || (t ? t('location.address-input-placeholder') : 'Enter address or location')}
        rightElement={<GoogleOfficialIcon boxSize={4} />}
        onChange={handleSelect}
        pr='4rem'
        bg='white'
        {...inputProps}
      />
    </Card>
  );
};

export default AddressSearchField;
